/**
 * The line and bar over a saved estimate's picture while its photoreal render
 * is on its way: the place in the queue, which picture is rendering and how
 * far, the time left, and how many pictures are in.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { RenderJob } from './types';

/** "under a minute" / "3 minutes". */
export function etaText(t: TFunction, sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec)) return null;
  if (sec < 60) return t('render.eta.underMinute');
  return t('render.eta.minutes', { count: Math.max(1, Math.round(sec / 60)) });
}

export function progressLine(t: TFunction, job: RenderJob | null, flags: { delayed: boolean; checking: boolean }): string | null {
  if (flags.checking) return t('render.progress.checking');
  if (!job) return null;
  switch (job.status) {
    case 'awaiting-upload': return t('render.progress.awaitingUpload');
    case 'queued':
      if (flags.delayed) return t('render.progress.delayed');
      return job.queueAhead > 0 ? t('render.progress.queuedAhead', { count: job.queueAhead }) : t('render.progress.queuedFirst');
    case 'claimed': return t('render.progress.claimed');
    case 'rendering': {
      if (job.stage === 'uploading') return t('render.progress.uploading');
      if (job.stage === 'downloading' || job.stage === 'starting' || !job.picture) return t('render.progress.claimed');
      const picture = t(`render.picturesIn.${job.picture.id}`);
      const eta = etaText(t, job.etaSec);
      return eta
        ? t('render.progress.rendering', { picture, percent: job.picture.percent, eta })
        : t('render.progress.renderingNoEta', { picture, percent: job.picture.percent });
    }
    case 'done': return t('render.progress.done');
    case 'failed': return t('render.progress.failed');
    default: return null;
  }
}

export interface RenderProgressProps {
  job: RenderJob | null;
  /** Pictures in, of the whole set. */
  done: number;
  total: number;
  delayed: boolean;
  checking: boolean;
}

export const RenderProgress: React.FC<RenderProgressProps> = ({ job, done, total, delayed, checking }) => {
  const { t } = useTranslation('results');
  const line = progressLine(t, job, { delayed, checking });
  if (!line) return null;
  const percent = job?.status === 'done' ? 100 : Math.max(0, Math.min(100, job?.percent ?? 0));
  const failed = job?.status === 'failed';
  return (
    <div className={`rdock-progress${failed ? ' is-failed' : ''}`} role="status" aria-live="polite">
      <div className="rdock-line">{line}</div>
      {!failed && (
        <div className="rdock-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={t('render.progress.barLabel')}>
          <div className="rdock-bar-fill" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className="rdock-count">{t('render.progress.ready', { done, total })}</div>
    </div>
  );
};
